import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { toast } from '@/hooks/use-toast'
import { useState } from 'react'
import AuthLayout from '@/views/auth-layout'
import { Card } from '@/components/ui/card'
import { resetPasswordConfirm } from '@/api/auth'
import { AxiosError } from 'axios'
import { PasswordInput } from '@/components/password-input'
import { Route } from '@/routes/auth/reset-password-confirm.$uidb64.$token' // Import Route for useParams type

export default function ResetPasswordConfirmPage() {
  const { t } = useTranslation()
  const nav = useNavigate()
  const { uidb64, token } = useParams({ from: Route.id })
  const [isLoading, setIsLoading] = useState(false)

  const formSchema = z
    .object({
      new_password: z.string().min(8, { message: t('new_password_too_short') }),
      confirm_new_password: z.string().min(1, { message: t('confirm_new_pass') }),
    })
    .refine((data) => data.new_password === data.confirm_new_password, {
      message: t('passwords_do_not_match'),
      path: ['confirm_new_password'],
    })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      new_password: '',
      confirm_new_password: '',
    },
  })

  async function onSubmit(data: z.infer<typeof formSchema>) {
    setIsLoading(true)
    try {
      await resetPasswordConfirm(
        uidb64,
        token,
        data.new_password,
        data.confirm_new_password,
      )
      toast({
        title: t('password_reset_success_title'),
        description: t('password_reset_success_desc'),
      })
      nav({ to: '/auth/login' })
    } catch (e) {
      if (e instanceof AxiosError) {
        toast({
          variant: 'destructive',
          title: e.response?.data?.error ?? t('serv_error'),
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthLayout>
      <Card className="p-6">
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('reset_password_title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('reset_password_desc')}
          </p>
        </div>
        <div className="grid gap-6 pt-4">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="grid gap-2">
                <FormField
                  control={form.control}
                  name="new_password"
                  render={({ field }) => (
                    <FormItem className="space-y-1 text-start">
                      <FormLabel>{t('new_pass')}</FormLabel>
                      <FormControl>
                        <PasswordInput
                          placeholder={t('new_pass_placeholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirm_new_password"
                  render={({ field }) => (
                    <FormItem className="space-y-1 text-start">
                      <FormLabel>{t('confirm_new_pass')}</FormLabel>
                      <FormControl>
                        <PasswordInput
                          placeholder={t('confirm_new_pass_placeholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button className="mt-2" disabled={isLoading}>
                  {isLoading ? t('resetting_password') : t('reset_password')}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </Card>
    </AuthLayout>
  )
}
